import React, { useState } from 'react'
import { ConfigurationPanel } from '../../../legacy-design-system/configuration-panel'
import { css } from '@emotion/css'
import { useCurrentOrganization, useSplitClient } from 'contrastBase/state'
import { useIntl } from 'react-intl'
import { ToggleSettingsItem } from '../../toggle-settings-item'
import { formatStyles } from '../../react-intl-helpers'
import { Checkbox } from '../../../legacy-design-system/checkbox'
import { useFetchAiSettings } from './api/use-fetch-ai-settings'
import { Text } from '@contrast-security-inc/design-system-typography'
import {
  AiUpdateSettingsArgs,
  updateAiSettings
} from './api/use-update-ai-settings'
import { useContrastMutation } from '../../../hooks/use-contrast-mutation'
import { BaseApiResponse } from '../../../api/shared'
import { HTTPError } from '../../../api/fetch/decorate-fetch'

export const ANTHROPIC_TERMS_LINK =
  'https://www.contrastsecurity.com/hubfs/Anthropic-on-Bedrock_Commercial_Terms.pdf'

export function AiSettingsError() {
  const intl = useIntl()

  return (
    <div className="org-settings-mfe-panel">
      <ConfigurationPanel title="title_orgSettings_aimlSettings">
        <span
          className={css`
            padding: 20px 35px 0px;
          `}
        >
          <Text>{intl.formatMessage({ id: 'detail_aiSettings_error' })}</Text>
        </span>
      </ConfigurationPanel>
    </div>
  )
}

function AimlOrganizationSettingsInner({
  initIntelligentRemediationGuidanceEnabled,
  initIntelligentRemediationGuidanceDisclaimerEnabled
}: {
  readonly initIntelligentRemediationGuidanceEnabled: boolean
  readonly initIntelligentRemediationGuidanceDisclaimerEnabled: boolean
}) {
  const intl = useIntl()
  const currentOrg = useCurrentOrganization()
  const [guidanceEnabled, setGuidanceEnabled] = useState(
    initIntelligentRemediationGuidanceEnabled
  )
  const [disclaimerEnabled, setDisclaimerEnabled] = useState(
    initIntelligentRemediationGuidanceDisclaimerEnabled
  )

  const organizationId = currentOrg.currentOrganization.organizationUuid

  const { mutate: updateAiSettingsMutation } = useContrastMutation<
    BaseApiResponse,
    HTTPError,
    AiUpdateSettingsArgs
  >(updateAiSettings)

  return (
    <div className="org-settings-mfe-panel">
      <ConfigurationPanel title="title_orgSettings_aimlSettings">
        <span
          className={css`
            padding: 20px 35px 0px;
          `}
        >
          <ToggleSettingsItem
            e2eId={'enable-intelligence-remediation-guidance-toggle'}
            title={intl.formatMessage({
              id: 'title_intelligentRemediationGuidance'
            })}
            description={intl.formatMessage({
              id: 'detail_intelligentRemediationGuidanceDescription'
            })}
            descriptionLink={intl.formatMessage({
              id: 'detail_learnMoreCapitalized'
            })}
            linkUrlId={'link_intelligentRemediationGuidance'}
            onToggle={(enabled) => {
              updateAiSettingsMutation(
                {
                  organizationId,
                  intelligentRemediationGuidanceEnabled: enabled,
                  intelligentRemediationGuidanceDisclaimerEnabled:
                    disclaimerEnabled
                },
                {
                  onSuccess: () => {
                    setGuidanceEnabled(enabled)
                  }
                }
              )
            }}
            enabled={guidanceEnabled}
          />
        </span>
        <span
          className={css`
            padding: 16px 35px 0px;
          `}
        >
          {intl.formatMessage(
            {
              id: 'detail_intelligentRemediationGuidanceDisclaimer'
            },
            {
              ...formatStyles.link({
                href: ANTHROPIC_TERMS_LINK,
                isExternal: true
              })
            }
          )}
        </span>
        <span
          className={css`
            padding: 16px 35px 40px;
          `}
        >
          <Checkbox
            disabled={!guidanceEnabled}
            id="show-ai-dislaimer-for-all-users"
            label={intl.formatMessage({
              id: 'detail_showAIDisclaimerForEveryone_checkbox'
            })}
            onChange={(enabled) => {
              updateAiSettingsMutation(
                {
                  organizationId,
                  intelligentRemediationGuidanceEnabled: guidanceEnabled,
                  intelligentRemediationGuidanceDisclaimerEnabled: enabled
                },
                {
                  onSuccess: () => {
                    setDisclaimerEnabled(enabled)
                  }
                }
              )
            }}
            value={disclaimerEnabled}
          />
        </span>
      </ConfigurationPanel>
    </div>
  )
}

export function AimlOrganizationSettings({
  aimlServiceEnabled
}: {
  readonly aimlServiceEnabled: boolean
}) {
  const { isSplitTreatmentOn } = useSplitClient()
  const ffAIHowToFix = isSplitTreatmentOn('PROD-3064_ai_how_to_fix')

  const currentOrg = useCurrentOrganization()
  const initialSettings = useFetchAiSettings({
    organizationId: currentOrg.currentOrganization.organizationUuid,
    enabled: aimlServiceEnabled && ffAIHowToFix
  })

  if (!ffAIHowToFix || !aimlServiceEnabled) {
    return null
  }

  if (initialSettings.error) {
    return <AiSettingsError />
  }

  if (!initialSettings.isLoading && initialSettings.data) {
    return (
      <AimlOrganizationSettingsInner
        initIntelligentRemediationGuidanceEnabled={
          initialSettings.data.settings.intelligent_remediation_guidance_enabled
        }
        initIntelligentRemediationGuidanceDisclaimerEnabled={
          initialSettings.data.settings
            .intelligent_remediation_guidance_disclaimer_enabled
        }
      />
    )
  }

  return null
}
